import { ref } from 'vue';
import NotificationService from '@/service/Project/NotificationService_old';
import { useEcho } from '@/composables/useEcho';

// Shared singleton state
const unreadCount = ref(0);
const listeners = new Set();

// Echo channel subscription (singleton)
let subscription = null;
let subscriberCount = 0;
let usingFallbackPoll = false;
let pollingPauseCount = 0;

function notifyListeners() {
    listeners.forEach(cb => cb(unreadCount.value));
}

export function useNotifications() {
    const fetchUnreadCount = async () => {
        if (pollingPauseCount > 0) return unreadCount.value;

        try {
            const response = await NotificationService.getUnreadCount();
            if (response.success) {
                unreadCount.value = response.data.count;
                notifyListeners();
            }
            return unreadCount.value;
        } catch {
            return unreadCount.value;
        }
    };

    const incrementUnreadCount = () => {
        unreadCount.value++;
        notifyListeners();
    };

    const decrementUnreadCount = () => {
        if (unreadCount.value > 0) {
            unreadCount.value--;
            notifyListeners();
        }
    };

    const setUnreadCount = (count) => {
        unreadCount.value = count;
        notifyListeners();
    };

    const pauseBackgroundPolling = () => {
        pollingPauseCount++;
    };

    const resumeBackgroundPolling = () => {
        pollingPauseCount = Math.max(0, pollingPauseCount - 1);
        if (pollingPauseCount === 0) fetchUnreadCount();
    };

    const subscribe = (callback) => {
        listeners.add(callback);
        return () => listeners.delete(callback);
    };

    // Connect to the user's private notification channel via WebSocket.
    // The caller is responsible for providing the authenticated user's ID.
    const startRealtime = (userId) => {
        subscriberCount++;
        if (subscription || usingFallbackPoll || !userId) return;

        // Reverb isn't configured in this environment (no env file sets
        // VITE_REVERB_APP_KEY yet) — skip straight to polling instead of
        // opening a WebSocket that can only ever fail.
        if (!import.meta.env.VITE_REVERB_APP_KEY) {
            usingFallbackPoll = true;
            _startFallbackPoll();
            return;
        }

        try {
            const echo = useEcho().getOrCreate();
            subscription = echo
                .private(`notifications.${userId}`)
                .listen('.notification.created', (data) => {
                    incrementUnreadCount();
                    notifyListeners();
                    // Emit a DOM event so NotificationsDropdown can append the item
                    window.dispatchEvent(new CustomEvent('notification:received', { detail: data }));
                });
        } catch (err) {
            console.warn('[useNotifications] WebSocket unavailable, falling back to poll:', err);
            usingFallbackPoll = true;
            _startFallbackPoll();
        }
    };

    const stopRealtime = () => {
        subscriberCount--;
        if (subscriberCount > 0) return;
        subscriberCount = 0;

        if (usingFallbackPoll) {
            usingFallbackPoll = false;
            _stopFallbackPoll();
            return;
        }

        if (!subscription) return;
        subscription = null;
        // Leave channel; Echo manages the underlying socket lifecycle
        try {
            const echo = useEcho().getOrCreate();
            echo.leaveAllChannels();
        } catch { /* already disconnected */ }
    };

    // ── Fallback polling (only used when WebSocket fails) ──────────────────
    let _fallbackInterval = null;

    const _startFallbackPoll = () => {
        if (_fallbackInterval) return;
        _fallbackInterval = setInterval(fetchUnreadCount, 30000);
    };

    const _stopFallbackPoll = () => {
        if (_fallbackInterval) {
            clearInterval(_fallbackInterval);
            _fallbackInterval = null;
        }
    };

    // Legacy names kept for call-sites that haven't been updated yet
    const startPolling = _startFallbackPoll;
    const stopPolling  = _stopFallbackPoll;

    return {
        unreadCount,
        fetchUnreadCount,
        incrementUnreadCount,
        decrementUnreadCount,
        setUnreadCount,
        pauseBackgroundPolling,
        resumeBackgroundPolling,
        subscribe,
        startRealtime,
        stopRealtime,
        // legacy aliases
        startPolling,
        stopPolling,
    };
}
